import { Button } from "@astryxdesign/core/Button";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/Stack";
import { useStudio, useStudioLanguages } from "@/features/studio/StudioRoot";
import { mediaUrl } from "@/lib/env";
import { coverMedia, galleryMedia } from "@/lib/media";
import { myBookings } from "@/lib/storage";
import { formatDateTime, groupWeekdays, todayIn, weekdayShortIso } from "@/lib/format";
import { useBookingNav } from "@/features/booking/useBookingNav";
import { ServiceList } from "./ServiceList";
import { Highlights } from "./Highlights";
import { WorksGallery } from "./WorksGallery";
import { StudioMap } from "@/features/about/StudioMap";
import { InfoBlock } from "@/features/about/InfoBlock";
import { InstallHint } from "@/features/about/InstallHint";
import { MetadataList, MetadataListItem } from "@astryxdesign/core/MetadataList";
import { ScrollText } from "lucide-react";
import { LanguageSwitch } from "@/components/layout/LanguageSwitch";
import { Empty } from "@/components/states";
import { useEffect, useRef } from "react";
import { useT } from "@/lib/i18n";

export function HomeScreen() {
  const studio = useStudio();
  const t = useT();
  const languages = useStudioLanguages();
  const nav = useBookingNav();
  const cover = coverMedia(studio);
  const gallery = galleryMedia(studio);
  const upcoming = myBookings.list(studio.slug).filter((b) => new Date(b.startsAt) > new Date()).slice(0, 1)[0];
  const popular = studio.services.slice(0, 4);
  const titleRef = useRef<HTMLSpanElement>(null);
  const c = studio.config.contacts;
  const todayIso = new Date(`${todayIn(studio.timezone)}T12:00:00Z`).getUTCDay() || 7;
  const todayHours = studio.hours
    .filter((h) => h.weekday === todayIso)
    .map((h) => `${h.opens.slice(0, 5)}–${h.closes.slice(0, 5)}`)
    .join(", ");
  const byDay = new Map<number, string[]>();
  for (const h of studio.hours) byDay.set(h.weekday, [...(byDay.get(h.weekday) ?? []), `${h.opens.slice(0, 5)}–${h.closes.slice(0, 5)}`]);
  const { cancellation, prepayment } = studio.config.policies;
  const links = [
    c.whatsapp && { label: "WhatsApp", href: c.whatsapp },
    c.telegram && { label: "Telegram", href: c.telegram },
    c.instagram && { label: "Instagram", href: c.instagram },
    c.vk && { label: "ВКонтакте", href: c.vk },
    c.map_url && { label: t.about.map, href: c.map_url },
    c.website && { label: t.about.website, href: c.website },
  ].filter(Boolean) as { label: string; href: string }[];
  useEffect(() => {
    document.title = studio.name;
  }, [studio.name]);

  return (
    <VStack as="main" gap={5} paddingInline={4} paddingBlockStart={3} className="app-safe-top app-safe-x page-with-nav">
      {languages.length > 1 ? (
        <HStack hAlign="end">
          <LanguageSwitch />
        </HStack>
      ) : null}
      <figure className="hero-media hero-tall w-full">
        {cover ? <img src={mediaUrl(cover.path)} alt={cover.alt} fetchPriority="high" /> : null}
        <span className="hero-scrim" aria-hidden="true" />
        <figcaption className="hero-text">
          <VStack gap={1}>
            <span ref={titleRef} tabIndex={-1}>
              <Heading level={1} type="display-3" color="inherit">
                {studio.name}
              </Heading>
            </span>
            {studio.config.tagline ? (
              <Text type="body" color="inherit">
                {studio.config.tagline}
              </Text>
            ) : null}
          </VStack>
        </figcaption>
      </figure>

      <Button label={t.home.book} variant="primary" size="lg" width="100%" onClick={() => nav.open()} />

      <VStack gap={2} hAlign="center">
        <Heading level={2}>{t.home.directions}</Heading>
        <Highlights studio={studio} label={t.home.directions} />
      </VStack>

      {upcoming ? (
        <VStack gap={1}>
          <Text type="label" color="secondary">
            {t.home.upcoming}
          </Text>
          <Button
            label={`${upcoming.serviceName} · ${formatDateTime(upcoming.startsAt, studio.timezone)}`}
            variant="secondary"
            width="100%"
            href={`/s/${studio.slug}/b#t=${upcoming.token}`}
          />
        </VStack>
      ) : null}

      {popular.length ? (
        <VStack gap={2} padding={4} className="home-block">
          <HStack hAlign="between" vAlign="center">
            <Heading level={2}>{t.home.services}</Heading>
            <Button label={t.home.allServices} variant="ghost" size="sm" href={`/s/${studio.slug}/services`} />
          </HStack>
          <ServiceList studio={studio} services={popular} onPick={(s) => nav.open(s.id)} />
        </VStack>
      ) : (
        <Empty title={t.home.noServicesTitle} description={t.home.noServicesText} />
      )}

      {gallery.length ? (
        <VStack gap={2}>
          <Heading level={2}>{t.home.works}</Heading>
          <WorksGallery items={gallery.slice(0, 6)} />
        </VStack>
      ) : null}

      {/* On a wide screen: "Как нас найти" on the left, rules and install hint stacked on the right. */}
      <VStack gap={4} className="home-info">
        {c.address || c.phone ? (
          <VStack gap={4} padding={4} className="find-us">
            <Heading level={2}>{t.home.findUs}</Heading>
            <VStack gap={2}>
              {c.address ? <Text type="body">{c.address}</Text> : null}
              <MetadataList>
                {groupWeekdays((d) => byDay.get(d)?.join(", ") ?? t.about.dayOff).map((g) => (
                  <MetadataListItem key={g.from} label={g.from === g.to ? weekdayShortIso(g.from) : `${weekdayShortIso(g.from)}–${weekdayShortIso(g.to)}`}>
                    {g.value}
                  </MetadataListItem>
                ))}
              </MetadataList>
              <Text type="supporting">{todayHours ? t.home.todayHours(todayHours) : t.home.closedToday}</Text>
            </VStack>
            {c.address ? <StudioMap address={c.address} geo={c.geo} /> : null}
            <VStack gap={2}>
              {c.phone ? <Button label={t.about.call(c.phone)} variant="primary" width="100%" href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} /> : null}
              {links.length ? (
                <HStack gap={2} wrap="wrap">
                  {links.map((l) => (
                    <Button key={l.href} label={l.label} variant="secondary" size="sm" href={l.href} target="_blank" rel="noopener noreferrer" />
                  ))}
                </HStack>
              ) : null}
            </VStack>
          </VStack>
        ) : null}

        {cancellation || prepayment ? (
          <InfoBlock icon={ScrollText} title={t.about.rules}>
            <VStack gap={3}>
              {cancellation ? <Text type="body">{cancellation}</Text> : null}
              {prepayment ? <Text type="body">{prepayment}</Text> : null}
            </VStack>
          </InfoBlock>
        ) : null}

        <InstallHint name={studio.config.short_name} />
      </VStack>
    </VStack>
  );
}
